import { sortBy } from 'lodash';
import {
  QueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import {
  CreateFormOptionPayload,
  CreateFormQuestionPayload,
  FormTemplateApi,
  FormThemeImageUploadResponse,
  UpdateFormQuestionPositionPayload,
  UploadThemeImagePayload,
} from 'api/form';
import {
  FormChoiceOption,
  FormQuestion,
  FormTemplate,
  FormTemplateAutomationUpdate,
  FormThemeImageType,
  ProjectTemplatePhase,
} from 'api/form/types';
import { TemplateId } from 'api/models';
import { toastService } from 'components/molecules/toast';
import { flattenFormTemplate } from 'components/organisms/templates/forms/utils';
import { useTranslation } from 'hooks/use-translation';
import { oneDay } from './constants';

export const getFormTemplateListQueryKey = (): string[] => ['form-templates'];
export const getFormTemplateQueryKey = (templateId: TemplateId) => [
  'form_template',
  ...templateId,
];
export const getFormTemplateImagesQueryKey = (templateId: TemplateId) => [
  'form_template_images',
  ...templateId,
];
export const getFormAutomationOptionsQueryKey = (): string[] => [
  'form-automation-options',
];

export const useFormTemplatesListQuery = (
  queryConfig: UseQueryOptions<
    FormTemplate[],
    unknown,
    FormTemplate[],
    string[]
  > = {},
): UseQueryResult<FormTemplate[], unknown> =>
  useQuery(
    getFormTemplateListQueryKey(),
    () => {
      return FormTemplateApi.getFormTemplateList().then((response) => {
        return response.templates;
      });
    },
    {
      staleTime: oneDay,
      ...queryConfig,
    },
  );

export const useDeleteFormTemplateMutation = (): UseMutationResult<
  unknown,
  unknown,
  { templateId: number }
> => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, { templateId: number }>(
    ({ templateId }) =>
      FormTemplateApi.deleteFormTemplate(TemplateId(templateId)),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getFormTemplateListQueryKey());
      },
    },
  );
};

export const useDuplicateFormTemplateMutation = (): UseMutationResult<
  FormTemplate,
  unknown,
  { templateId: number }
> => {
  const queryClient = useQueryClient();

  return useMutation<FormTemplate, unknown, { templateId: number }>(
    ({ templateId }) => FormTemplateApi.duplicateFormTemplate(templateId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(getFormTemplateListQueryKey());
      },
    },
  );
};

export const useFormTemplatesQuery = (
  templateId: TemplateId,
): UseQueryResult<FormTemplate, unknown> => {
  return useQuery({
    retry: false,
    refetchOnWindowFocus: false,
    queryKey: getFormTemplateQueryKey(templateId),
    queryFn: async (): Promise<FormTemplate> => {
      const data = await FormTemplateApi.getFormTemplate(templateId);

      return flattenFormTemplate(data);
    },
  });
};

export const useCreateFormTemplate = (): UseMutationResult<
  FormTemplate,
  unknown,
  Partial<FormTemplate>
> => {
  const queryClient = useQueryClient();

  return useMutation<FormTemplate, unknown, Partial<FormTemplate>>(
    (createFormTemplatePayload) =>
      FormTemplateApi.createFormTemplate(createFormTemplatePayload),
    {
      onSuccess: (data) => {
        queryClient.setQueryData(
          getFormTemplateQueryKey(TemplateId(data.templateId)),
          data,
        );

        queryClient.invalidateQueries(getFormTemplateListQueryKey());
      },
    },
  );
};

export const useGetFlattenedFormQuestions = (
  templateId: number,
): FormQuestion[] => {
  const queryClient = useQueryClient();
  const formTemplate = queryClient.getQueryData<FormTemplate>(
    getFormTemplateQueryKey(TemplateId(templateId)),
  );

  return formTemplate ? formTemplate.formQuestions : [];
};
export const useCreateFieldQuestionMutation = (
  templateId: number,
): UseMutationResult<FormQuestion, unknown, CreateFormQuestionPayload> => {
  const queryClient = useQueryClient();

  const errorTitle = useTranslation('failure');
  const errorMessage = useTranslation(
    'form_templates.create_question_error_message',
  );

  return useMutation<FormQuestion, unknown, CreateFormQuestionPayload>(
    (payload) => FormTemplateApi.createFormQuestion(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getFormTemplateQueryKey(TemplateId(templateId)),
        );
      },
      onError: () => {
        toastService({
          title: errorTitle,
          message: errorMessage,
        }).error();
      },
    },
  );
};

export const useDeleteFieldQuestionMutation = ({
  templateId,
  questionText,
}: {
  templateId: number;
  questionText: string;
}): UseMutationResult<unknown, unknown, { questionId: number }> => {
  const queryClient = useQueryClient();

  const successTitle = useTranslation('removed_successfully');
  const successMessage = useTranslation(
    'form_templates.delete_question_success_message',
    { questionText },
  );

  return useMutation<unknown, unknown, { questionId: number }>(
    ({ questionId }) => FormTemplateApi.deleteFormQuestion(questionId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getFormTemplateQueryKey(TemplateId(templateId)),
        );

        toastService({
          toastContainer: {},
          title: successTitle,
          message: successMessage,
        }).success();
      },
    },
  );
};

const updateFormQuestion = (
  formQuestions: FormQuestion[],
  questionId: number,
  payload: Partial<FormQuestion>,
) => {
  const { options: optionPayload, ...rest } = payload;

  return formQuestions.map((question) =>
    question.questionId === questionId
      ? {
          ...question,
          ...rest,
          options: sortBy(
            (question.options || []).map((option) => {
              const updatedOptionPayload = (optionPayload || []).find(
                (payloadData) => payloadData.optionId === option.optionId,
              );

              return {
                ...option,
                ...updatedOptionPayload,
              };
            }),
            'position',
          ),
        }
      : question,
  );
};

const handleFormQuestionUpdate = ({
  templateId,
  questionId,
  payload,
  queryClient,
}: {
  templateId: number;
  questionId: number;
  payload: Partial<FormQuestion>;
  queryClient: QueryClient;
}) => {
  const previous = queryClient.getQueryData<FormTemplate>(
    getFormTemplateQueryKey(TemplateId(templateId)),
  );

  if (previous) {
    const updatedFormQuestions = updateFormQuestion(
      previous.formQuestions,
      questionId,
      payload,
    );

    queryClient.setQueryData(getFormTemplateQueryKey(TemplateId(templateId)), {
      ...previous,
      formQuestions: updatedFormQuestions,
    });
  }

  return previous;
};

type UpdateFormQuestionPayload = Partial<FormQuestion>;
export const useUpdateFieldQuestionMutation = (
  templateId: number,
): UseMutationResult<
  FormQuestion,
  unknown,
  { questionId: number; payload: UpdateFormQuestionPayload }
> => {
  const queryClient = useQueryClient();

  return useMutation<
    FormQuestion,
    unknown,
    { questionId: number; payload: UpdateFormQuestionPayload }
  >(
    ({ questionId, payload }) =>
      FormTemplateApi.updateFormQuestion(questionId, payload),
    {
      onMutate: async ({ questionId, payload }) => {
        return handleFormQuestionUpdate({
          templateId,
          questionId,
          payload,
          queryClient,
        });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getFormTemplateQueryKey(TemplateId(templateId)),
        );
      },
    },
  );
};

export const useUpdateFieldQuestionPositionMutation = (
  templateId: number,
): UseMutationResult<
  FormQuestion,
  unknown,
  { questionId: number; payload: UpdateFormQuestionPositionPayload }
> => {
  const queryClient = useQueryClient();

  return useMutation<
    FormQuestion,
    unknown,
    { questionId: number; payload: UpdateFormQuestionPositionPayload }
  >(
    ({ questionId, payload }) =>
      FormTemplateApi.updateFormQuestionPosition(questionId, payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getFormTemplateQueryKey(TemplateId(templateId)),
        );
      },
    },
  );
};

const updateLastUpdatedTime = ({
  updatedFormTemplate,
  templateId,
  queryClient,
}: {
  updatedFormTemplate: FormTemplate;
  templateId: number;
  queryClient: QueryClient;
}) => {
  const previous = queryClient.getQueryData<FormTemplate>(
    getFormTemplateQueryKey(TemplateId(templateId)),
  );

  if (previous) {
    const updatedValue = {
      ...previous,
      updatedAt: updatedFormTemplate.updatedAt,
      updatedBy: updatedFormTemplate.updatedBy,
    };

    queryClient.setQueryData(
      getFormTemplateQueryKey(TemplateId(templateId)),
      updatedValue,
    );
  }
};

type UpdateFormTemplatePayload = Partial<FormTemplateAutomationUpdate>;

export const useFormTemplateUpdateMutation = (
  templateId: number,
): UseMutationResult<FormTemplate, unknown, UpdateFormTemplatePayload> => {
  const queryClient = useQueryClient();

  return useMutation<
    FormTemplate,
    unknown,
    UpdateFormTemplatePayload,
    { previous: FormTemplate | undefined }
  >(
    (payload) =>
      FormTemplateApi.updateFormTemplate(payload, TemplateId(templateId)),
    {
      onMutate: async (payload) => {
        const previous = queryClient.getQueryData<FormTemplate>(
          getFormTemplateQueryKey(TemplateId(templateId)),
        );

        if (previous) {
          const updatedValue = {
            ...previous,
            ...payload,
          };

          queryClient.setQueryData(
            getFormTemplateQueryKey(TemplateId(templateId)),
            updatedValue,
          );
        }

        return { previous };
      },
      onSuccess: async (response, payload) => {
        updateLastUpdatedTime({
          updatedFormTemplate: response,
          templateId,
          queryClient,
        });

        if (payload && (payload.name || payload.description)) {
          await queryClient.invalidateQueries(getFormTemplateListQueryKey());
        }
      },
    },
  );
};

export const useCreateFieldOptionMutation = (
  templateId: number,
): UseMutationResult<FormChoiceOption, unknown, CreateFormOptionPayload> => {
  const queryClient = useQueryClient();

  return useMutation<FormChoiceOption, unknown, CreateFormOptionPayload>(
    (payload) => FormTemplateApi.createFormOption(payload),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getFormTemplateQueryKey(TemplateId(templateId)),
        );
      },
    },
  );
};

export const useDeleteOptionMutation = ({
  templateId,
  optionLabel,
}: {
  templateId: number;
  optionLabel: string;
}): UseMutationResult<unknown, unknown, { optionId: number }> => {
  const queryClient = useQueryClient();

  const successTitle = useTranslation('removed_successfully');
  const successMessage = useTranslation(
    'form_templates.delete_option_succes_message',
    { optionLabel },
  );

  return useMutation<unknown, unknown, { optionId: number }>(
    ({ optionId }) => FormTemplateApi.deleteOption(optionId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getFormTemplateQueryKey(TemplateId(templateId)),
        );

        toastService({
          toastContainer: {},
          title: successTitle,
          message: successMessage,
        }).success();
      },
    },
  );
};

type UpdateFormOptionPayload = Partial<FormChoiceOption>;
export const useUpdateFieldOptionMutation = (
  templateId: number,
): UseMutationResult<
  FormChoiceOption,
  unknown,
  { questionId: number; optionId: number; payload: UpdateFormOptionPayload }
> => {
  const queryClient = useQueryClient();

  return useMutation<
    FormChoiceOption,
    unknown,
    { questionId: number; optionId: number; payload: UpdateFormOptionPayload }
  >(
    ({ optionId, payload }) =>
      FormTemplateApi.updateFormOption(optionId, payload),
    {
      onMutate: async ({ questionId, optionId, payload }) => {
        return handleFormQuestionUpdate({
          templateId,
          questionId,
          payload: {
            options: [
              {
                optionId,
                ...payload,
              },
            ] as FormChoiceOption[],
          },
          queryClient,
        });
      },
    },
  );
};

interface UpdateFieldOptionPositionPayload {
  questionId: number;
  optionId: number;
  updatedOptionsList: FormChoiceOption[];
}
export const useUpdateFieldOptionPositionMutation = (
  templateId: number,
): UseMutationResult<
  FormChoiceOption,
  unknown,
  UpdateFieldOptionPositionPayload
> => {
  const queryClient = useQueryClient();

  return useMutation<
    FormChoiceOption,
    unknown,
    UpdateFieldOptionPositionPayload
  >(
    ({ optionId, updatedOptionsList }) => {
      const updatedOption = updatedOptionsList.find(
        (option) => option.optionId === optionId,
      );
      const payload = {
        optionId,
        position: updatedOption?.position || 0,
      };

      return FormTemplateApi.updateFormOptionPosition(optionId, payload);
    },
    {
      onMutate: async ({ questionId, updatedOptionsList }) => {
        return handleFormQuestionUpdate({
          templateId,
          questionId,
          payload: {
            options: updatedOptionsList,
          },
          queryClient,
        });
      },
    },
  );
};

export const useDeleteOptionBranchingMutation = ({
  templateId,
  optionLabel,
}: {
  templateId: number;
  optionLabel: string;
}): UseMutationResult<unknown, unknown, { optionId: number }> => {
  const queryClient = useQueryClient();

  const successTitle = useTranslation('removed_successfully');
  const successMessage = useTranslation(
    'form_templates.delete_branch_success_message',
    { optionLabel },
  );

  return useMutation<unknown, unknown, { optionId: number }>(
    ({ optionId }) => FormTemplateApi.deleteOptionBranch(optionId),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getFormTemplateQueryKey(TemplateId(templateId)),
        );

        toastService({
          toastContainer: {},
          title: successTitle,
          message: successMessage,
        }).success();
      },
    },
  );
};

export const useUploadThemeImageMutation = (
  templateId: number,
  options: UseMutationOptions<
    FormThemeImageUploadResponse,
    unknown,
    UploadThemeImagePayload
  > = {},
): UseMutationResult<
  FormThemeImageUploadResponse,
  unknown,
  UploadThemeImagePayload
> => {
  const queryClient = useQueryClient();

  return useMutation<
    FormThemeImageUploadResponse,
    unknown,
    UploadThemeImagePayload
  >(({ file, data }) => FormTemplateApi.uploadFormThemeImage({ file, data }), {
    onSuccess: async () => {
      await queryClient.invalidateQueries(
        getFormTemplateQueryKey(TemplateId(templateId)),
      );
    },
    ...options,
  });
};

export const useDeleteThemeImageMutation = (
  templateId: number,
): UseMutationResult<unknown, unknown, { imageType: FormThemeImageType }> => {
  const queryClient = useQueryClient();

  return useMutation<unknown, unknown, { imageType: FormThemeImageType }>(
    ({ imageType }) =>
      FormTemplateApi.deleteThemeAttachment(templateId, imageType),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getFormTemplateQueryKey(TemplateId(templateId)),
        );
      },
    },
  );
};

export const useFormAutomationProjectTemplateOptions = (
  queryConfig: UseQueryOptions<
    ProjectTemplatePhase[],
    unknown,
    ProjectTemplatePhase[],
    string[]
  > = {},
): UseQueryResult<ProjectTemplatePhase[], unknown> =>
  useQuery(
    getFormAutomationOptionsQueryKey(),
    () => {
      return FormTemplateApi.getFormAutomationProjectTemplateOptions().then(
        (response) => {
          return response.optionList;
        },
      );
    },
    {
      staleTime: oneDay,
      refetchOnWindowFocus: false,
      ...queryConfig,
    },
  );
